import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import {
  isArray,
  isBoolean,
  isNumber,
  isDate,
  isObject,
  isString,
  isNull,
  get,
} from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import MyButton from '@common/components/Button/MyButton';
import GroupedParameters from '@common/components/GroupedParameters/GroupedParameters';
import MyCheckbox from '@common/components/MyCheckbox/MyCheckbox';

import {
  isModalCalcOrSum,
  createPathNameForTextCheckbox,
  createPathNameForCheckbox,
  isNodeList,
  isNodeObj,
  isListSourceNotSelected,
  getShowStateForArrayWithSource,
  isArrayWithSelectedSource,
  getShowStateForList,
  getValidPropKeyForNodeObj,
  getParentPathForNodeObj,
  getShowStateForNodeObj,
  isFieldActualPlaceholder,
} from '@common/helpers/placeholderModalHelpers';

const TextPlaceholderModal = ({
  documentJson,
  showModal,
  currentValue = '',
  onOkClick,
  onCancelClick,
}) => {
  const [checkedField, setCheckedField] = useState();

  const { t } = useTranslation();

  useEffect(() => {
    isModalCalcOrSum(showModal)
      ? setCheckedField([])
      : setCheckedField(currentValue);
  }, [showModal, currentValue]);

  const getDocTypeRecursive = (providedDocument, propKey, modalShowState) => {
    let currentField = get(providedDocument, propKey);

    if (isNodeList(currentField, modalShowState, propKey)) {
      // render a list with it's node
      return (
        <GroupedParameters>
          <Typography variant='subtitle2' sx={{ mb: '4px' }}>{`${t(
            'this_is_list'
          )} ${propKey}`}</Typography>
          {getDocumentFieldsRecursive(
            currentField[0],
            getShowStateForList(modalShowState)
          )}
        </GroupedParameters>
      );
    }

    if (isNodeObj(currentField, modalShowState)) {
      let validPropKey = getValidPropKeyForNodeObj(propKey);
      let parentPath = getParentPathForNodeObj({ propKey, modalShowState });

      // render object with it's node
      return (
        <GroupedParameters style={{ margin: '10px' }}>
          <Typography variant='subtitle2' sx={{ mb: '4px' }}>{`${t(
            'this_is_obj'
          )}: ${parentPath}${validPropKey}`}</Typography>
          {getDocumentFieldsRecursive(
            currentField,
            getShowStateForNodeObj({ modalShowState, propKey })
          )}
        </GroupedParameters>
      );
    }

    if (isArrayWithSelectedSource(currentField, modalShowState, propKey)) {
      // render nested list with it's node
      return (
        <Box style={{ margin: '10px' }}>
          {getDocumentFieldsRecursive(
            currentField,
            getShowStateForArrayWithSource({
              modalShowState,
              propKey,
            })
          )}
        </Box>
      );
    }

    if (isFieldActualPlaceholder(currentField, modalShowState)) {
      const checkboxFieldName = createPathNameForCheckbox(
        modalShowState,
        propKey
      );
      // render checkbox for selection of placeholder
      return (
        <MyCheckbox
          label={propKey}
          name={checkboxFieldName}
          parent={modalShowState.parent}
          checked={checkedField === checkboxFieldName}
          onChange={changeSelected}
        />
      );
    }

    return <div />;
  };

  const getDocumentFieldsRecursive = (
    providedDocument = {},
    modalShowState
  ) => {
    let objKeys = Object.keys(providedDocument);
    if (modalShowState.show) {
      if (isListSourceNotSelected(objKeys, modalShowState)) {
        return <Box>{t('list_is_not_found')}</Box>;
      }

      return objKeys.map((propKey, index) => {
        return (
          <Box key={index}>
            {getDocTypeRecursive(providedDocument, propKey, modalShowState)}
          </Box>
        );
      });
    }
  };

  const changeSelected = (data) => {
    let newValue = data.name === checkedField ? '' : data.name;

    setCheckedField(newValue);
  };

  const onOkClicked = () => {
    onOkClick(checkedField);
  };

  const onCancel = () => {
    setCheckedField(currentValue);
    onCancelClick();
  };

  return (
    <Dialog fullWidth maxWidth={'sm'} open={showModal.show}>
      <DialogTitle>{t('modal_header')}</DialogTitle>
      <DialogContent>
        {isModalCalcOrSum(showModal) ? (
          <Grid columns={2} divided>
            {getDocumentFieldsRecursive(documentJson, showModal)}
          </Grid>
        ) : (
          getDocumentFieldsRecursive(documentJson, showModal)
        )}
      </DialogContent>
      <DialogActions>
        <MyButton onClick={onCancel}>{t('cancel')}</MyButton>
        <MyButton color={'primary'} onClick={onOkClicked}>
          {t('ok')}
        </MyButton>
      </DialogActions>
    </Dialog>
  );
};

TextPlaceholderModal.propTypes = {
  documentJson: PropTypes.object,
  showModal: PropTypes.shape({
    show: PropTypes.bool,
    listSource: PropTypes.string,
    parent: PropTypes.string,
    showRootFields: PropTypes.bool,
    action: PropTypes.string,
  }),
  setShowModal: PropTypes.func,
  currentValue: PropTypes.string,
  currentFormula: PropTypes.string,
  onCalcSave: PropTypes.func,
  setProp: PropTypes.func,
};

export default TextPlaceholderModal;
